import { Box, Flex, Link, Spinner } from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { Breadcrumbs, Card, GridTable, Text } from '@diamond/shared/ui';
import { AdminLayout } from '@diamond/sol-admin/components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ColDef, RowClassParams } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { getLHCItemsAsRows } from '../api';
import { LHCDetailEmptyState } from '../components/lhc-detail-empty-state';
import { LHCDetailFilterBar } from '../components/lhc-detail-filter-bar';
import { LHCDetailHeader } from '../components/lhc-detail-header';
import { LHCDetailSummary } from '../components/lhc-detail-summary';
import { PLANT_OPTIONS } from '../constants';
import { useCollectionPlants, useLHCDetail } from '../hooks';
import type { LHCDetailInvoiceRowData } from '../types';
import { buildLhcItemsFilter } from '../utils/build-lhc-params';

const links = [
  { title: 'Beranda', link: '/' },
  { title: 'Collection Management', link: '/collection-management' },
  { title: 'Detail LHC', link: '#' },
];

const TABLE_HEIGHT = 'calc(100vh - 420px)';
const TABLE_MIN_HEIGHT = '400px';
const TABLE_EMPTY_HEIGHT = '100px';

const CHILD_ROW_BG = '#F1F1F1';

function POCellRenderer(params: {
  value: string;
  data: LHCDetailInvoiceRowData;
  context?: { toggleExpand: (rowId: string) => void };
}) {
  if (params.data?.isChildRow) {
    if (!params.value) return null;
    return (
      <Flex align="center" h="100%">
        <Text fontSize="xs" color="gray.600">
          {params.value}
        </Text>
      </Flex>
    );
  }

  const isExpanded = params.data?.isExpanded;

  return (
    <Flex direction="column" justify="center" h="100%">
      <Text fontSize="sm" fontWeight={600}>
        {params.value}
      </Text>
      {params.data?.hasChildren && (
        <Flex
          align="center"
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (params.data?.rowId && params.context?.toggleExpand) {
              params.context.toggleExpand(params.data.rowId);
            }
          }}
        >
          <Text fontSize="xs" color="#0000A3" fontWeight={500}>
            Surat jalan lain
          </Text>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              color: '#0000A3',
              transform: isExpanded ? 'rotate(180deg)' : undefined,
              transition: 'transform 0.2s',
            }}
          />
        </Flex>
      )}
    </Flex>
  );
}

function PaymentProofCellRenderer(params: {
  value: boolean;
  data: LHCDetailInvoiceRowData;
}) {
  if (params.data?.isChildRow || !params.value) return '-';
  return (
    <Flex align="center" h="100%">
      <Link color="#0000A3" fontWeight={500} cursor="pointer">
        Lihat
      </Link>
    </Flex>
  );
}

function getRowStyle(params: RowClassParams<LHCDetailInvoiceRowData>) {
  if (params.data?.isChildRow) {
    return { background: CHILD_ROW_BG };
  }
  return undefined;
}

const columnDefs: ColDef<LHCDetailInvoiceRowData>[] = [
  {
    field: 'no',
    headerName: 'No.',
    width: 60,
    pinned: 'left',
    resizable: false,
    valueFormatter: ({ value }) =>
      typeof value === 'number' ? String(value) : value || '',
  },
  {
    field: 'poNumber',
    headerName: 'PO',
    width: 220,
    pinned: 'left',
    cellRenderer: POCellRenderer,
    autoHeight: true,
  },
  { field: 'deliveryOrderNo', headerName: 'Surat Jalan/SO', width: 200 },
  { field: 'plant', headerName: 'Plant', width: 100 },
  { field: 'invoiceDate', headerName: 'Tgl. Faktur', width: 110 },
  { field: 'invoiceNo', headerName: 'No. Faktur', width: 140 },
  { field: 'billTo', headerName: 'Bill to', width: 100 },
  { field: 'customerName', headerName: 'Nama Customer', width: 180 },
  { field: 'outletName', headerName: 'Nama Outlet', width: 160 },
  { field: 'bankMutationNo', headerName: 'No. Mutasi', width: 150 },
  { field: 'poValue', headerName: 'Nilai PO (Rp)', width: 130 },
  { field: 'soValue', headerName: 'Nilai SO (Rp)', width: 130 },
  { field: 'invoiceValue', headerName: 'Nilai Faktur (Rp)', width: 150 },
  { field: 'differenceAmount', headerName: 'Selisih +/-', width: 130 },
  { field: 'mutationValue', headerName: 'Nilai Mutasi (Rp)', width: 150 },
  { field: 'depositUsed', headerName: 'Pakai Deposit', width: 140 },
  {
    field: 'hasPaymentProof',
    headerName: 'Bukti Trf',
    width: 100,
    cellRenderer: PaymentProofCellRenderer,
    sortable: false,
  },
  { field: 'bank', headerName: 'Bank', width: 120 },
  { field: 'remark', headerName: 'Keterangan', width: 150 },
];

export function LHCDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Plant options
  const { data: collectionPlantsData } = useCollectionPlants();
  const plantOptions = useMemo(() => {
    if (collectionPlantsData?.data?.length) {
      return collectionPlantsData.data.map((p) => ({
        label: p.name,
        value: p.id,
      }));
    }
    return PLANT_OPTIONS;
  }, [collectionPlantsData]);

  // Date filter form (required by DateFilter component)
  const methods = useForm({
    defaultValues: {
      'invoice_date-from': '',
      'invoice_date-to': '',
    },
  });

  const dateFrom = methods.watch('invoice_date-from');
  const dateTo = methods.watch('invoice_date-to');

  const handleDateFilter = useCallback((period: string) => {
    return { fromDate: '', toDate: '' };
  }, []);

  const updateDateParams = useCallback(
    (fromDate: string, toDate: string) => {
      methods.setValue('invoice_date-from', fromDate);
      methods.setValue('invoice_date-to', toDate);
    },
    [methods]
  );

  // Filter state
  const [plantFilter, setPlantFilter] = useState<SelectOption | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const handleSearch = useCallback(() => {
    setAppliedSearch(searchValue);
  }, [searchValue]);

  const apiParams = useMemo(
    () =>
      buildLhcItemsFilter({
        dateFrom,
        dateTo,
        plantFilter,
        appliedSearch,
      }),
    [dateFrom, dateTo, plantFilter, appliedSearch]
  );

  // Fetch LHC detail with server-side filtering
  const { data: detailData, isLoading } = useLHCDetail(id || '', apiParams);

  const tableData = useMemo<LHCDetailInvoiceRowData[]>(() => {
    if (!detailData) return [];
    return getLHCItemsAsRows(detailData);
  }, [detailData]);

  // Track collapsed parent rows (default: all expanded)
  const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((rowId: string) => {
    setCollapsedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  // Filter rows: hide child rows only when parent is collapsed
  const visibleRows = useMemo<LHCDetailInvoiceRowData[]>(() => {
    return tableData.reduce<LHCDetailInvoiceRowData[]>((acc, row) => {
      if (!row.isChildRow) {
        const isExpanded = row.hasChildren && !collapsedRows.has(row.rowId);
        acc.push({ ...row, isExpanded });
      } else if (row.parentId && !collapsedRows.has(row.parentId)) {
        acc.push(row);
      }
      return acc;
    }, []);
  }, [tableData, collapsedRows]);

  const hasInvoices = visibleRows.length > 0;

  const handleBack = useCallback(() => {
    navigate('/collection-management');
  }, [navigate]);

  const handleAddSuratJalan = useCallback(() => {
    // UI-only stub
  }, []);

  const handleSync = useCallback(() => {
    // UI-only stub
  }, []);

  if (isLoading) {
    return (
      <AdminLayout>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="lg" />
        </Flex>
      </AdminLayout>
    );
  }

  if (!detailData) {
    return (
      <AdminLayout>
        <Flex justify="center" align="center" minH="400px">
          <Text>Data LHC tidak ditemukan</Text>
        </Flex>
      </AdminLayout>
    );
  }

  const { lhc } = detailData;

  return (
    <AdminLayout maxWidth="100%">
      <Box paddingBottom={4}>
        <Breadcrumbs links={links} useRouterDom />
      </Box>

      <Box py={4}>
        <LHCDetailHeader lhc={lhc} onBack={handleBack} onSync={handleSync} />

        <LHCDetailSummary lhc={lhc} />

        <LHCDetailFilterBar
          methods={methods}
          handleDateFilter={handleDateFilter}
          updateDateParams={updateDateParams}
          plantValue={plantFilter}
          onPlantChange={setPlantFilter}
          plantOptions={plantOptions}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onSearch={handleSearch}
          onAddSuratJalan={handleAddSuratJalan}
        />

        <Card p="0" m="0">
          <GridTable<LHCDetailInvoiceRowData>
            columnDefs={columnDefs}
            rowData={visibleRows}
            isLoading={isLoading}
            pagination={false}
            height={hasInvoices ? TABLE_HEIGHT : TABLE_EMPTY_HEIGHT}
            minHeight={hasInvoices ? TABLE_MIN_HEIGHT : TABLE_EMPTY_HEIGHT}
            gridOptions={{
              defaultColDef: {
                resizable: true,
                sortable: true,
              },
              domLayout: 'normal',
              suppressNoRowsOverlay: true,
              getRowStyle,
              context: { toggleExpand },
            }}
          />
        </Card>

        {!hasInvoices && !isLoading && (
          <LHCDetailEmptyState onSearchSuratJalan={handleAddSuratJalan} />
        )}
      </Box>
    </AdminLayout>
  );
}

export default LHCDetail;
